import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, Checkbox, Button, Modal } from 'antd';
import { showMessageError } from '../../../utils/RenderUtils';
import styles from './login.module.less';
import { isNil } from 'lodash';
import { getCaptcha } from '@Modules/account/auth/AuthActions';
import { useDispatch } from 'react-redux';
import { InfoCircleOutlined, RedoOutlined } from '@ant-design/icons';
import inTrackLogo from '../../../../static/images/intrack/black-logo.png';
import dkLogo from '../../../../static/images/flavors/dk/dk-logo.png';
import mciLogo from '../../../../static/images/flavors/mci/mci-logo-login-page.png';
import snappLogo from '../../../../static/images/flavors/snapp/snapp-logo.png';
import ozoneLogo from '../../../../static/images/flavors/ozone/ozone-logo.png';
import { detectFlavor, flavors } from '@Utils/FlavorUtils';
import { thirdPartyEnums } from '@Modules/admin/thirdParty/components/ThirdPartyTypes';
import qs from 'qs';

export function getMetadata() {
  const flavor = detectFlavor();

  const result = { logo: inTrackLogo, flavor, buttonStyle: {} };

  switch (flavor) {
    case flavors.INTRACK:
      result['logo'] = inTrackLogo;
      break;
    case flavors.DK:
      result['logo'] = dkLogo;
      result['buttonStyle'] = {
        backgroundColor: '#F05060',
        borderColor: '#F05060',
      };
      break;
    case flavors.MCI:
      result['logo'] = mciLogo;
      break;
    case flavors.SNAPP:
      result['logo'] = snappLogo;
      result['buttonStyle'] = {
        backgroundColor: '#00E261',
        borderColor: '#00E261',
      };
      break;
    case flavors.OZONE:
      result['logo'] = ozoneLogo;
      result['buttonStyle'] = {
        backgroundColor: '#6537FF',
        borderColor: '#6537FF',
      };
      break;
  }

  return result;
}

const SignIn = (props) => {
  const { search } = useLocation();
  const dispatch = useDispatch();

  const [email, setEmail] = useState(
    isNil(props.registerResponse)
      ? props?.currentUser?.email ?? ''
      : props.registerResponse.email
  );

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);
  const [captchaEnabled, setCaptchaEnabled] = useState(null);
  const [captchaCode, setCaptchaCode] = useState(null);

  const history = useHistory();

  const [remember, setRemember] = useState(false);

  const userHasActiveProduct = (currentUser) => {
    return (
      (currentUser && currentUser.login && currentUser.lastProduct) ||
      (currentUser?.products &&
        Array.isArray(currentUser?.products) &&
        currentUser?.products.length > 0 &&
        currentUser?.products[0].id)
    );
  };

  const resendCaptcha = () => {
    dispatch(getCaptcha(setToken, setCaptchaEnabled));
    setCaptchaCode(null);
  };

  const redirectToVerification = ({ otpId }) => {
    const params = qs.stringify({ otpId, remember }, { addQueryPrefix: true });

    history.push('/auth/verify' + params);
  };

  useEffect(() => {
    resendCaptcha();
  }, []);

  const fetchLogin = () => {
    props.loginFetch({
      login: {
        username: email,
        password: password,
        rememberMe: remember,
        captchaToken: captchaEnabled ? token : null,
        captchaCode: captchaEnabled ? captchaCode?.trim() : null,
      },
      resendCaptcha,
      cb: redirectToVerification,
      onUnlockException: () => setUnlockOpen(true),
    });
  };

  const handlePressEnter = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      fetchLogin();
    }
  };

  const onSignup = () => {
    showMessageError({
      status: 'Error',
      message: 'Please contact admin',
    });
  };

  useEffect(() => {
    if (!props.returnUrl || props.returnUrl?.startsWith('/auth')) {
      return;
    }

    const searchParams = new URLSearchParams(search);

    if (props.currentUser?.login) return;

    searchParams.set('returnUrl', props.returnUrl);

    history.replace(`/auth/login?${searchParams.toString()}`);
  }, [props.currentUser, props.returnUrl]);

  useEffect(() => {
    const activeProduct = userHasActiveProduct(props.currentUser);
    const currentPath = location.pathname;

    const searchParams = new URLSearchParams(location.search);

    const returnUrl = searchParams.get('returnUrl');

    if (
      activeProduct &&
      currentPath.startsWith('/auth') &&
      props.currentUser?.emailVerified
    ) {
      let activeProductObject = props.currentUser.products.find(
        (x) => x.id === activeProduct
      );

      if (returnUrl) {
        history.push(returnUrl);
      } else if (
        activeProductObject.thirdPartyOption !== thirdPartyEnums.PARENT
      ) {
        history.push(`/product/${activeProduct}/users/overview`);
      } else {
        history.push(`/admin/third-party/${activeProduct}/list`);
      }
    } else if (!props.currentUser?.emailVerified && props.currentUser?.login) {
      history.push(`/account/profile/update`);
    }
  }, [props.currentUser]);

  const { logo, buttonStyle, flavor } = getMetadata();

  return (
    <Fragment>
      <div className={styles[`home-page-${flavor}`]}>
        <div className={styles[`home-page__box-${flavor}`]}>
          <div
            className={`${styles['home-page__left-section']} ${
              styles[`home-page__left-section-${flavor}`]
            }`}
          >
            <div className={styles.form}>
              <img
                width={flavor === flavors.MCI ? 80 : 150}
                src={logo}
                style={{ margin: '0 auto 40px' }}
                alt={'logo'}
              />
              <div className={styles.form__item}>
                <div className={styles.form__title}>Email</div>
                <Input
                  id="int-login-username"
                  onKeyDown={(ev) => handlePressEnter(ev)}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter Email"
                  className={styles.form__input}
                />
              </div>
              <div className={styles.form__item}>
                <div className={styles.form__title}>Password</div>
                <Input.Password
                  id="int-login-password"
                  onKeyDown={(ev) => handlePressEnter(ev)}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className={styles.form__input}
                />
              </div>

              <div
                className={styles['form__captcha-container']}
                style={{ display: captchaEnabled ? '' : 'none' }}
              >
                <div className={` ${styles.captchaContainer}`}>
                  <img
                    id="captcha-image"
                    alt="captcha"
                    style={{ borderRadius: '3px 0px 0px 3px' }}
                  />
                  <div
                    onClick={props.recaptchaLoading ? null : resendCaptcha}
                    className={styles.reCaptcha}
                  >
                    <RedoOutlined />
                  </div>
                </div>

                <div>
                  <Input
                    id="int-login-captcha"
                    onKeyDown={(ev) => handlePressEnter(ev)}
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder="Enter Captcha Code"
                    className={styles.form__input}
                  />
                </div>
              </div>

              <div className={styles['form__remember-container']}>
                <div>
                  <Checkbox
                    id="int-login-remember"
                    value={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className={styles['form__remember-me']}
                  >
                    Remember Me
                  </Checkbox>
                </div>
                <div>
                  <div
                    onClick={() => history.push('/auth/reset-password')}
                    className={styles['form__reset-password']}
                  >
                    Reset Password
                  </div>
                </div>
              </div>
              <div className={styles['form__button-container']}>
                <Button
                  id="int-login-submit"
                  loading={props.loginLoading}
                  onClick={fetchLogin}
                  type="primary"
                  className={styles.form__login}
                  data-badge="inline"
                  style={buttonStyle}
                >
                  LOGIN
                </Button>
                <div className={styles.form__singup}>
                  <span className={styles.form__singup__description}>
                    Don&apos;t have an account?
                  </span>
                  <span
                    onClick={onSignup}
                    className={styles.form__singup__button}
                  >
                    Sign Up
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            id={`login-side-${flavor}`}
            className={styles[`home-page__right-section-${flavor}`]}
          />
        </div>
      </div>
      <Modal
        footer={null}
        title={null}
        closeIcon={<Fragment />}
        open={unlockOpen}
        onCancel={() => setUnlockOpen(false)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <InfoCircleOutlined style={{ fontSize: 72, color: '#FC5F64' }} />
          <p style={{ textAlign: 'center' }}>
            Incorrect password. Your account was disabled due to several failed
            attempts. Please reset your password.
          </p>
          <Button
            type="primary"
            style={{ width: '100%' }}
            onClick={() => setUnlockOpen(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
};
export default SignIn;
